import { useState } from "react";
import {
  ArrowRight,
  ChevronDown,
  CheckCircle,
  MapPin,
  Globe,
  Search,
  Calendar,
  Shield,
  Clock,
  MessageCircle,
} from "lucide-react";
import {
  ABN,
  DARK_BG,
  DARK_BORDER,
  WA_LINK,
  type NavigateFn,
} from "../lib/site";
import { Label, OrangeLine } from "../components/ui/Label";
import WhatsAppIcon from "../components/ui/WhatsAppIcon";
import Footer from "../components/layout/Footer";
import { trackCtaClick } from "../utils/analytics";

export default function HomePage({ navigate }: { navigate: NavigateFn }) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs = [
    {
      q: "Do you work with my trade or clinic?",
      a: "Yes — if your business lives on local phone calls and Google searches. We work with trades, clinics, home services, medical, beauty, fitness, and similar operators across Australia. If someone in your suburb googles what you do and you want the phone to ring, we can help.",
    },
    {
      q: "What types of businesses do you work with?",
      a: "Primarily local service businesses that win work through calls and enquiries — plumbers, electricians, clinics, cleaners, landscapers, and the like. We also help other local operators when the goal is the same: show up in the right suburbs and make it easy to get in touch.",
    },
    {
      q: "How long does it take to get a new website live?",
      a: "Most business websites are live within 2–4 weeks from sign-off. We move fast so you can get back to running the business.",
    },
    {
      q: "What does local SEO actually mean for my business?",
      a: "It means when someone in your target suburb searches for what you offer on Google, your business shows up — in the Maps pack, in the organic results, or both. We handle the suburb targeting, citations, and Google Business Profile to make that happen.",
    },
    {
      q: "Will my website work on mobile?",
      a: "Yes. Every site we build is mobile-first. Most of your calls come from a phone. Your site is built for that — tap-to-call, clear forms, fast load.",
    },
    {
      q: "Do I need to sign a long-term contract?",
      a: "No lock-in contracts. We earn your business by getting the phone to ring, not by trapping you in a deal.",
    },
    {
      q: "How is ONIN Digital different from other agencies?",
      a: "We only sell what moves the phone: website + suburb targeting + Google Business Profile + enquiry path. No brand fluff retainers. No lock-in. A clear plan, a straight-forward quote, and a system built for local operators who need calls — not another brochure site.",
    },
  ];

  const proofItems = [
    "Registered Australian Business",
    "Australia-Wide Service",
    "Suburb + Maps Focused",
    "100% Mobile-First",
    "All States & Territories",
    "No Lock-In Contracts",
    "Registered Australian Business",
    "Australia-Wide Service",
    "Suburb + Maps Focused",
    "100% Mobile-First",
    "All States & Territories",
    "No Lock-In Contracts",
  ];

  return (
    <>
      <section
        className="relative min-h-screen flex items-center pt-16 overflow-hidden"
        style={{ backgroundColor: DARK_BG }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(255,85,0,0.035) 0px, rgba(255,85,0,0.035) 1px, transparent 1px, transparent 64px), repeating-linear-gradient(90deg, rgba(255,85,0,0.035) 0px, rgba(255,85,0,0.035) 1px, transparent 1px, transparent 64px)",
          }}
        />
        <div
          className="absolute top-1/4 right-[-8%] w-[550px] h-[550px] rounded-full blur-[140px] pointer-events-none"
          style={{ backgroundColor: "rgba(255,85,0,0.07)" }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24 w-full">
          <div className="max-w-5xl">
            <Label>For local businesses that live on phone calls</Label>

            <h1
              className="font-display font-black text-[clamp(3.5rem,11vw,9rem)] leading-[0.87] uppercase tracking-tight mt-6 mb-6"
              style={{ color: "#F6F3EE" }}
            >
              Stop Losing
              <br />
              <span style={{ color: "#FF5500" }}>Customers</span>
              <br />
              Online.
            </h1>

            <p
              className="text-lg sm:text-xl max-w-lg mb-10 leading-relaxed font-light"
              style={{ color: "rgba(246,243,238,0.60)" }}
            >
              If locals search for what you do and find someone else first, you don&apos;t have a
              website problem — you have a visibility problem. We fix the site, Maps, and enquiry
              path so the phone rings.
            </p>

            <div className="flex flex-wrap gap-3 mb-20">
              <button
                type="button"
                onClick={() => {
                  trackCtaClick("home_hero_get_quote", "/contact");
                  navigate("/contact");
                }}
                className="flex items-center gap-2 bg-[#FF5500] text-black font-bold px-7 py-4 text-sm tracking-wide hover:bg-[#E64D00] transition-colors"
              >
                GET A QUOTE <ArrowRight size={15} />
              </button>
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCtaClick("home_hero_whatsapp", WA_LINK)}
                className="flex items-center gap-2 font-bold px-7 py-4 text-sm tracking-wide transition-colors"
                style={{
                  border: "1px solid rgba(255,255,255,0.20)",
                  color: "#F6F3EE",
                }}
              >
                <WhatsAppIcon size={16} className="text-[#25D366]" />
                CHAT ON WHATSAPP
              </a>
            </div>

            <div
              className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8"
              style={{ borderTop: `1px solid rgba(255,255,255,0.08)` }}
            >
              {[
                { num: "2019", label: "In business since" },
                { num: "2–4 wks", label: "Typical go-live" },
                { num: "ABN", label: "Registered AU business" },
                { num: "Maps + site", label: "Built for calls" },
              ].map((p) => (
                <div key={p.label}>
                  <div
                    className="font-display font-black text-3xl leading-none mb-1"
                    style={{ color: "#FF5500" }}
                  >
                    {p.num}
                  </div>
                  <div
                    className="text-[11px] tracking-wider uppercase"
                    style={{ color: "rgba(246,243,238,0.40)" }}
                  >
                    {p.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#FF5500] py-3.5 overflow-hidden">
        <div className="animate-marquee">
          {proofItems.map((item, i) => (
            <span
              key={i}
              className="text-black font-bold text-xs tracking-[0.2em] uppercase flex-shrink-0 px-6"
            >
              {item}
              <span className="ml-6 opacity-40">·</span>
            </span>
          ))}
        </div>
      </section>

      <section className="py-24 border-b border-black/[0.07] bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <Label>What makes us different</Label>
              <h2 className="font-display font-black text-5xl sm:text-6xl uppercase leading-[0.88] text-foreground mt-4 mb-5">
                Not an agency.
                <br />
                <span className="text-[#FF5500]">
                  A growth
                  <br />
                  system.
                </span>
              </h2>
              <OrangeLine />
              <p className="text-muted-foreground leading-relaxed mb-5">
                Most agencies sell brochure websites. We build one system that gets the phone to
                ring: a fast site, suburb-level Maps visibility, a sharp Google Business Profile,
                and an enquiry path people actually use.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Built for Australian trades, clinics, and home-service operators — businesses that
                win when someone nearby searches, taps Call, and books you.
              </p>
            </div>
            <div className="space-y-3">
              {[
                {
                  icon: <Globe size={18} />,
                  title: "Websites That Generate Leads",
                  desc: "Not brochures. Fast, mobile-first sites with tap-to-call and forms built to convert visitors into enquiries.",
                },
                {
                  icon: <MapPin size={18} />,
                  title: "Local Search Dominance",
                  desc: "Rank in the suburbs you want to serve. Show up on Google Maps when locals search for what you offer.",
                },
                {
                  icon: <Search size={18} />,
                  title: "Google Business Profile",
                  desc: "Your GBP is often your strongest local asset. We set it up and optimise it for the Map pack.",
                },
                {
                  icon: <Calendar size={18} />,
                  title: "Enquiry Systems That Work",
                  desc: "Contact forms, booking flows, and click-to-call setups that make it easy for customers to reach you.",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex gap-4 p-5 bg-white border border-black/[0.07] hover:border-[#FF5500]/30 transition-colors group cursor-default"
                >
                  <div className="text-[#FF5500] flex-shrink-0 mt-0.5">{item.icon}</div>
                  <div>
                    <div className="font-bold text-sm mb-1 text-foreground">{item.title}</div>
                    <div className="text-sm text-muted-foreground leading-relaxed">
                      {item.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 border-b border-black/[0.07] bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <Label>Core Offer</Label>
            <h2 className="font-display font-black text-4xl sm:text-6xl uppercase leading-tight text-foreground mt-4">
              Website + Local SEO.
              <br />
              <span className="text-[#FF5500]">The complete package.</span>
            </h2>
            <p className="text-muted-foreground text-sm mt-4 max-w-md mx-auto leading-relaxed">
              One system so when someone in your suburb googles what you do, they find you — and
              can call in one tap.
            </p>
          </div>
          <div className="max-w-3xl mx-auto bg-white border border-[#FF5500]/25 relative">
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#FF5500]" />
            <div className="p-8 sm:p-12">
              <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
                <div>
                  <div className="font-display font-black text-3xl uppercase text-foreground">
                    Website + Local SEO Bundle
                  </div>
                  <div className="text-muted-foreground text-sm mt-1">
                    Built to turn local search into phone calls
                  </div>
                </div>
                <div className="bg-[#FF5500] text-black font-bold text-[10px] tracking-widest px-3 py-1.5 self-start">
                  MOST POPULAR
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mb-10">
                {[
                  "Fast, mobile-first website",
                  "Google Business Profile setup",
                  "Local SEO for your suburbs",
                  "Click-to-call and WhatsApp integration",
                  "Contact / quote request forms",
                  "Google Maps optimisation",
                  "On-page SEO built-in",
                  "Monthly performance reporting",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle size={15} className="text-[#FF5500] flex-shrink-0" />
                    <span className="text-sm text-foreground/80">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    trackCtaClick("home_bundle_get_quote", "/contact");
                    navigate("/contact");
                  }}
                  className="flex items-center gap-2 bg-[#FF5500] text-black font-bold px-6 py-3 text-sm tracking-wide hover:bg-[#E64D00] transition-colors"
                >
                  GET A QUOTE <ArrowRight size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    trackCtaClick("home_bundle_see_packages", "/packages");
                    navigate("/packages");
                  }}
                  className="border border-black/20 text-foreground font-bold px-6 py-3 text-sm tracking-wide hover:border-black/40 transition-colors"
                >
                  SEE ALL PACKAGES
                </button>
                <button
                  type="button"
                  onClick={() => {
                    trackCtaClick("home_bundle_see_services", "/services");
                    navigate("/services");
                  }}
                  className="text-sm text-[#FF5500] font-bold tracking-wide flex items-center gap-1 hover:gap-2 transition-all px-2"
                >
                  See all services <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 border-b border-black/[0.07] bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <Label>Before you commit</Label>
            <h2 className="font-display font-black text-4xl sm:text-5xl uppercase leading-tight text-foreground mt-4">
              What you get
              <br />
              <span className="text-[#FF5500]">before you pay.</span>
            </h2>
            <p className="text-muted-foreground text-sm mt-4 leading-relaxed">
              No pressure pitch. A clear plan for your suburbs, a straight-forward quote, and a
              path that fits how local operators actually buy — including WhatsApp.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: <MessageCircle size={18} />,
                title: "WhatsApp-first chat",
                desc: "Start on your terms. Tell us your trade, suburbs, and what’s broken online.",
              },
              {
                icon: <Clock size={18} />,
                title: "2–4 week go-live",
                desc: "Typical timeline from sign-off to live site — so you’re not waiting months.",
              },
              {
                icon: <Shield size={18} />,
                title: "No lock-in contracts",
                desc: "We earn the work by getting calls in, not by locking you into a retainer.",
              },
              {
                icon: <CheckCircle size={18} />,
                title: "Registered AU business",
                desc: `${ABN}. Local team. Australia-wide service for phone-led operators.`,
              },
            ].map((item, i) => (
              <div
                key={i}
                className="p-6 bg-white border border-black/[0.07] hover:border-[#FF5500]/30 transition-colors"
              >
                <div className="text-[#FF5500] mb-4">{item.icon}</div>
                <div className="font-bold text-sm mb-2 text-foreground">{item.title}</div>
                <div className="text-sm text-muted-foreground leading-relaxed">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="py-24 border-b"
        style={{ backgroundColor: DARK_BG, borderColor: DARK_BORDER }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <Label>How it works</Label>
            <h2
              className="font-display font-black text-4xl sm:text-6xl uppercase mt-4 leading-tight"
              style={{ color: "#F6F3EE" }}
            >
              Enquiry to live
              <br />
              <span style={{ color: "#FF5500" }}>in weeks.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-4 gap-8 relative">
            <div
              className="hidden sm:block absolute top-8 left-[14%] right-[14%] h-px"
              style={{
                background:
                  "linear-gradient(to right, transparent, rgba(255,85,0,0.25), transparent)",
              }}
            />
            {[
              {
                step: "01",
                title: "Quick Chat",
                desc: "Tell us about the business and the suburbs you serve. We listen before we pitch.",
              },
              {
                step: "02",
                title: "Strategy & Quote",
                desc: "A clear plan for Maps, site, and enquiries — plus a straight-forward quote. No fluff.",
              },
              {
                step: "03",
                title: "Build & Optimise",
                desc: "We build the site and set up local SEO while you keep running the business.",
              },
              {
                step: "04",
                title: "Go Live & Grow",
                desc: "You go live. We track what’s working and keep optimising for more calls.",
              },
            ].map((s, i) => (
              <div key={i} className="text-center relative">
                <div className="w-16 h-16 bg-[#FF5500] flex items-center justify-center mx-auto mb-5 relative z-10">
                  <span className="font-display font-black text-xl text-black">{s.step}</span>
                </div>
                <div className="font-bold text-sm mb-2" style={{ color: "#F6F3EE" }}>
                  {s.title}
                </div>
                <div
                  className="text-sm leading-relaxed"
                  style={{ color: "rgba(246,243,238,0.50)" }}
                >
                  {s.desc}
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 justify-center mt-14">
            <button
              type="button"
              onClick={() => {
                trackCtaClick("home_process_quick_chat", "/contact");
                navigate("/contact");
              }}
              className="flex items-center gap-2 bg-[#FF5500] text-black font-bold px-6 py-3 text-sm tracking-wide hover:bg-[#E64D00] transition-colors"
            >
              START WITH A QUICK CHAT <ArrowRight size={14} />
            </button>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCtaClick("home_process_whatsapp", WA_LINK)}
              className="flex items-center gap-2 font-bold px-6 py-3 text-sm tracking-wide transition-colors"
              style={{
                border: "1px solid rgba(255,255,255,0.20)",
                color: "#F6F3EE",
              }}
            >
              <WhatsAppIcon size={16} className="text-[#25D366]" />
              WHATSAPP US
            </a>
          </div>
        </div>
      </section>

      <section className="py-24 border-b border-black/[0.07] bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Label>Who we help</Label>
          <h2 className="font-display font-black text-4xl sm:text-6xl uppercase leading-[0.9] text-foreground mt-4 mb-4">
            Built for operators
            <br />
            <span className="text-[#FF5500]">who need the phone to ring.</span>
          </h2>
          <p className="text-muted-foreground mb-10 max-w-lg text-sm leading-relaxed">
            If your week depends on local calls, Google searches, and showing up in the right
            suburbs — this is for you.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
            {["Trades", "Clinics", "Home Services"].map((segment, i) => (
              <div
                key={i}
                className="p-5 text-center bg-white border border-black/[0.08] font-bold text-sm tracking-wide uppercase text-foreground/70 hover:border-[#FF5500]/35 hover:text-foreground transition-all"
              >
                {segment}
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Also medical, beauty, fitness — and other local operators when the goal is more calls
            from search. Hospitality, retail, and professional services on request.
          </p>
        </div>
      </section>

      <section className="py-24 border-b border-black/[0.07] bg-muted">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Label>Common questions</Label>
          <h2 className="font-display font-black text-4xl sm:text-6xl uppercase leading-tight text-foreground mt-4 mb-12">
            Got a question?
            <br />
            <span className="text-[#FF5500]">Good.</span>
          </h2>
          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="border border-black/[0.08] bg-white hover:border-black/[0.15] transition-colors"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left gap-4"
                >
                  <span className="font-semibold text-sm text-foreground">{faq.q}</span>
                  <ChevronDown
                    size={15}
                    className={`flex-shrink-0 text-[#FF5500] transition-transform duration-200 ${openFaq === i ? "rotate-180" : ""}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed border-t border-black/[0.05] pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-28 bg-[#FF5500] relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(0,0,0,0.05) 0px, rgba(0,0,0,0.05) 1px, transparent 1px, transparent 48px), repeating-linear-gradient(90deg, rgba(0,0,0,0.05) 0px, rgba(0,0,0,0.05) 1px, transparent 1px, transparent 48px)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-display font-black text-5xl sm:text-8xl uppercase text-black leading-[0.85] mb-6">
            Ready to get
            <br />
            more calls?
          </h2>
          <p className="text-black/60 text-lg mb-10 max-w-sm mx-auto">
            15-minute chat. Clear plan. Quote with no lock-in.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              onClick={() => {
                trackCtaClick("home_footer_get_quote", "/contact");
                navigate("/contact");
              }}
              className="flex items-center gap-2 bg-black text-white font-bold px-8 py-4 text-sm tracking-wide hover:bg-[#1a1a1a] transition-colors"
            >
              GET A QUOTE <ArrowRight size={15} />
            </button>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCtaClick("home_footer_whatsapp", WA_LINK)}
              className="flex items-center gap-2 border-2 border-black text-black font-bold px-8 py-4 text-sm tracking-wide hover:bg-black hover:text-white transition-colors group"
            >
              <WhatsAppIcon size={16} className="text-[#25D366] group-hover:text-[#25D366]" />
              WHATSAPP US
            </a>
          </div>
        </div>
      </section>

      <Footer navigate={navigate} />
    </>
  );
}
